import { Box, Flex } from '@chakra-ui/react';
import { Checkbox } from '@kula-ai/components';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import moment from 'moment-timezone';
import type { ScheduleRejectionEmailProps } from '@/pages/ats-job-creation/tabs/JobPosting/auto-rejections/types';
import { TimeZoneSelectorV1 } from '@/components/formBuilder/formFields/DateField/components/TimeZoneSelectorV1';
import { CustomTimeSelectorTz } from '@/components/formBuilder/formFields/DateField/components/CustomTimeSelectorTz';
import { KText } from '@/ui-library/components';
import { KInput } from '@/ui-library/components/atoms/KInput';
import { getDefaultTimeZone } from '@/components/formBuilder/formFields/DateField/helpers';
import { useEffect } from 'react';
import { useDateFormat } from '@/hooks';

export const ScheduleRejectionEmail = ({
  formik,
}: ScheduleRejectionEmailProps) => {
  const currentUser = useSelector(selectCurrUser);
  const { time_zone } = currentUser || {};
  const { timeFormat } = useDateFormat();
  const { values, setValues, errors } = formik;
  const {
    sendAfter: sendAfterValue,
    scheduleTime,
    scheduleTimezone,
    scheduleEmail,
    // isScheduleEmail,
  } = values || {};

  const getCurrentTimeInTimezone = (timezoneKey: string) => {
    const now = moment().tz(timezoneKey);

    const minutes = Math.round(now.minute() / 15) * 15;
    now.minute(minutes % 60);

    if (minutes === 60) {
      now.add(1, 'hour').minute(0);
    }

    return now.format('HH:mm');
  };

  const defaultTimezone = getDefaultTimeZone(time_zone) as {
    key: string;
    label: string;
  };
  const defaultTimezoneKey = defaultTimezone.key;

  // Get current values from formik or defaults
  const checked = scheduleEmail ?? true;
  const sendAfter = sendAfterValue ?? 1;
  const time = scheduleTime || getCurrentTimeInTimezone(defaultTimezoneKey);
  const timezone = scheduleTimezone ?? defaultTimezone;

  useEffect(() => {
    // Only initialize values once when component mounts or when defaultTimezone changes
    // Don't react to scheduleEmail changes to avoid re-render loop
    if (scheduleEmail === undefined) {
      setValues({
        ...formik.values,
        scheduleTime: getCurrentTimeInTimezone(defaultTimezoneKey),
        scheduleTimezone: defaultTimezone,
        sendAfter: 1,
        scheduleEmail: true, // Default to true instead of using checked
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTimezoneKey, defaultTimezone, setValues]);

  const handleChecked = (e) => {
    const newChecked = e.target.checked;
    if (newChecked) {
      setValues({
        ...formik.values,
        scheduleTime: time,
        scheduleTimezone: timezone,
        sendAfter: sendAfter,
        scheduleEmail: true,
      });
    } else {
      setValues({
        ...formik.values,
        scheduleTime: null,
        scheduleTimezone: null,
        sendAfter: 1,
        scheduleEmail: false,
      });
    }
  };

  const handleSendAfter = (e) => {
    const valueStr = e.target.value.replace(/[^0-9]/g, '');
    let value = valueStr === '' ? '' : Number(valueStr);

    // Prevent 0
    if (value === 0 && valueStr !== '') {
      value = 1;
    }

    setValues({
      ...formik.values,
      sendAfter: value,
    });
  };

  const handleTime = (value: string) => {
    setValues({
      ...formik.values,
      scheduleTime: value,
    });
  };

  const handleTimezone = (value) => {
    const newTime = getCurrentTimeInTimezone(value.key);
    setValues({
      ...formik.values,
      scheduleTime: newTime,
      scheduleTimezone: value.key,
    });
  };

  return (
    <Box w="full" p="0">
      <Flex gap="8px" alignItems="center" mb="3">
        <Checkbox
          size="sm"
          label="Schedule rejection email"
          checked={checked}
          onChange={handleChecked}
        />
      </Flex>
      {checked && (
        <Flex gap={{ base: 2, md: 4 }} wrap="wrap" align="center">
          <Box w="155px" flexShrink={0}>
            <KInput
              // KInput derives the underlying input `type` from `fieldType`
              // (a plain `type` prop is overridden by it), so use `fieldType`
              // to actually render a numeric field.
              fieldType="number"
              min={1}
              value={sendAfter === '' ? '' : sendAfter}
              onChange={handleSendAfter}
              onBlur={(e) => {
                if (e.target.value === '' || Number(e.target.value) < 1) {
                  setValues({
                    ...formik.values,
                    sendAfter: 1,
                  });
                }
              }}
              error={errors?.sendAfter}
              size="md"
              bg="white"
              label="Send after"
              labelProps={{
                size: 'sm-medium',
                color: 'secondary-900',
              }}
              isRequired
              borderColor="border-color"
              borderRadius="md"
              px="3"
              py="2"
              customRightElementProps={{
                w: '90px',
                borderLeftWidth: '1px',
                borderColor: 'border-color',
                borderLeftRadius: '0',
                borderRightRadius: '6px',
                right: '1px',
                bg: 'secondary-50',
                mt: '0px',
                top: '0px',
                height: '42px',
                p: '2',
              }}
              CustomRightElement={() => <KText>weekdays</KText>}
            />
          </Box>
          <Box w="130px" flexShrink={0}>
            <CustomTimeSelectorTz
              date={null}
              value={time}
              onChange={handleTime}
              timeFormat={timeFormat}
              timeZone={timezone}
              inputProps={{
                menuPlacement: 'top',
              }}
            />
          </Box>
          <Box flex="1" minW="0">
            <TimeZoneSelectorV1 value={timezone} onChange={handleTimezone} />
          </Box>
        </Flex>
      )}
    </Box>
  );
};
